import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  TextField,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Divider,
} from "@mui/material";
import RoleAndPermissionStyles from "./RoleAndPermissionStyles";
import { PERMISSIONS } from "../../constants/Constants";

interface CreateRoleProps {
  open: boolean;
  onClose: () => void;
  onCreate: (role: {
    name: string;
    description: string;
    permissions: string[];
  }) => void;
  editMode?: boolean;
  initialData?: {
    name: string;
    description: string;
    permissions: string[];
  } | null;
}

const CreateRole: React.FC<CreateRoleProps> = ({
  open,
  onClose,
  onCreate,
  editMode = false,
  initialData = null,
}) => {
  const [roleName, setRoleName] = useState(initialData?.name || "");
  const [roleDescription, setRoleDescription] = useState(
    initialData?.description || ""
  );
  const [search, setSearch] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(
    initialData?.permissions || []
  );

  // Reset form when dialog opens/closes or initialData changes
  React.useEffect(() => {
    setRoleName(initialData?.name || "");
    setRoleDescription(initialData?.description || "");
    setSelectedPermissions(initialData?.permissions || []);
    setSearch("");
  }, [open, initialData]);

  const handlePermissionChange = (perm: string) => {
    setSelectedPermissions((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  };

  const handleCreate = () => {
    onCreate({
      name: roleName,
      description: roleDescription,
      permissions: selectedPermissions,
    });
    // Optionally reset form here
    setRoleName("");
    setRoleDescription("");
    setSelectedPermissions([]);
    setSearch("");
  };

  // Filter permissions by search
  const filteredPermissions = PERMISSIONS.map((group) => ({
    ...group,
    items: group.items.filter((item) =>
      item.label.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter((group) => group.items.length > 0);

  const isUnchanged =
    !!editMode &&
    !!initialData &&
    roleName === initialData.name &&
    roleDescription === initialData.description &&
    selectedPermissions.length === initialData.permissions.length &&
    selectedPermissions.every((perm) => initialData.permissions.includes(perm));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      id="role_dialog"
      data-test-id="test-role_dialog"
      aria-label={editMode ? "Edit Role Dialog" : "Create Role Dialog"}
    >
      <DialogTitle
        id="role_dialog_title"
        data-test-id="test-role_dialog_title"
        aria-label="Role Dialog Title"
      >
        <Typography
          variant="h6"
          fontWeight="bold"
          id="role_dialog_heading"
          data-test-id="test-role_dialog_heading"
          aria-label={editMode ? "Edit Role" : "Create New Role"}
        >
          {editMode ? "Edit Role" : "Create New Role"}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          id="role_dialog_subtitle"
          data-test-id="test-role_dialog_subtitle"
          aria-label={
            editMode
              ? "Modify role details and permissions"
              : "Create a new role with specific permissions"
          }
        >
          {editMode
            ? "Modify role details and permissions"
            : "Create a new role with specific permissions"}
        </Typography>
      </DialogTitle>

      <DialogContent
        id="role_dialog_content"
        data-test-id="test-role_dialog_content"
        aria-label="Role Dialog Content"
      >
        <Box
          sx={RoleAndPermissionStyles.createRoleDialogBox}
          id="role_form_box"
          data-test-id="test-role_form_box"
          aria-label="Role Form Box"
        >
          <TextField
            label="Role Name"
            placeholder="Enter role name"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            fullWidth
            required
            id="role_name_input"
            data-test-id="test-role_name_input"
            aria-label="Role Name Input"
            disabled={editMode}
          />
          <TextField
            label="Role Description"
            placeholder="Enter role description"
            value={roleDescription}
            onChange={(e) => setRoleDescription(e.target.value)}
            fullWidth
            required
            multiline
            minRows={2}
            id="role_description_input"
            data-test-id="test-role_description_input"
            aria-label="Role Description Input"
          />

          <Box
            id="role_permissions_section"
            data-test-id="test-role_permissions_section"
            aria-label="Assign Permissions Section"
          >
            <Typography
              fontWeight="bold"
              sx={{ mb: 1 }}
              id="assign_permissions_title"
              data-test-id="test-assign_permissions_title"
              aria-label="Assign Permissions Title"
            >
              Assign Permissions
            </Typography>

            <TextField
              placeholder="Search permissions..."
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={RoleAndPermissionStyles.createRoleSearchField}
              id="search_permissions_input"
              data-test-id="test-search_permissions_input"
              aria-label="Search Permissions Input"
            />

            <Box
              sx={RoleAndPermissionStyles.createRolePermissionsBox}
              id="permissions_group_container"
              data-test-id="test-permissions_group_container"
              aria-label="Permissions Group Container"
            >
              {filteredPermissions.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  id="no_permissions_found"
                  data-test-id="test-no_permissions_found"
                  aria-label="No permissions found"
                >
                  No permissions found.
                </Typography>
              )}

              {filteredPermissions.map((group, idx) => (
                <Box
                  key={group.group}
                  sx={RoleAndPermissionStyles.createRolePermissionGroup}
                  id={`permission_group_${group.group}`}
                  data-test-id={`test-permission_group_${group.group}`}
                  aria-label={`Permission Group ${group.group}`}
                >
                  <Typography
                    sx={RoleAndPermissionStyles.createRolePermissionGroupTitle}
                    id={`permission_group_title_${group.group}`}
                    data-test-id={`test-permission_group_title_${group.group}`}
                    aria-label={`Permission Group Title ${group.group}`}
                  >
                    {group.group}
                  </Typography>
                  <FormGroup
                    id={`formgroup_${group.group}`}
                    data-test-id={`test-formgroup_${group.group}`}
                    aria-label={`Form Group ${group.group}`}
                  >
                    {group.items.map((item) => (
                      <FormControlLabel
                        key={item.key}
                        control={
                          <Checkbox
                            checked={selectedPermissions.includes(item.key)}
                            onChange={() => handlePermissionChange(item.key)}
                            id={`checkbox_permission_${item.key}`}
                            data-test-id={`test-checkbox_permission_${item.key}`}
                            aria-label={`Permission ${item.label}`}
                          />
                        }
                        label={item.label}
                        id={`permission_label_${item.key}`}
                        data-test-id={`test-permission_label_${item.key}`}
                        aria-label={`Permission Label ${item.label}`}
                      />
                    ))}
                  </FormGroup>
                  {idx !== filteredPermissions.length - 1 && (
                    <Divider
                      sx={RoleAndPermissionStyles.createRolePermissionDivider}
                      id={`divider_permission_group_${group.group}`}
                      data-test-id={`test-divider_permission_group_${group.group}`}
                      aria-label={`Divider After ${group.group}`}
                    />
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        id="role_dialog_actions"
        data-test-id="test-role_dialog_actions"
        aria-label="Role Dialog Actions"
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: "4px" }}
          id="cancel_button"
          data-test-id="test-cancel_button"
          aria-label="Cancel Button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          sx={{ borderRadius: "4px" }}
          disabled={
            !roleName || !roleDescription || (editMode ? isUnchanged : false)
          }
          id="submit_button"
          data-test-id="test-submit_button"
          aria-label={editMode ? "Update Role Button" : "Create Role Button"}
        >
          {editMode ? "Update Role" : "Create Role"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateRole;
